import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import type { OrganizationPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

const CATEGORIES = [
  { value: "barrier", label: "Speed Barrier" },
  { value: "dugout", label: "Dugout" },
  { value: "pavilion", label: "Pavilion" },
  { value: "jersey", label: "Jersey" },
  { value: "other", label: "Other" },
];

export default function AssetTypeCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("barrier");
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [depthMm, setDepthMm] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [organizations, setOrganizations] = useState<OrganizationPublic[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getOrganizations().then((res) => {
      setOrganizations(res.organizations);
      if (res.organizations.length > 0) {
        setOrganizationId(res.organizations[0].id);
      }
    });
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const res = await api.createAssetType({
        organization_id: organizationId,
        name,
        description: description || null,
        category: category as "barrier" | "dugout" | "pavilion" | "jersey" | "other",
        width_mm: parseInt(widthMm, 10),
        height_mm: parseInt(heightMm, 10),
        depth_mm: depthMm ? parseInt(depthMm, 10) : null,
      });
      navigate(`/asset-types/${res.asset_type.id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create asset type");
    } finally {
      setSubmitting(false);
    }
  }

  const selectClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="flex flex-col gap-6" data-page="asset-type-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Asset Type</h1>
        <p className="text-muted-foreground">Define a new type of advertising asset</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Asset Type Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="asset-type-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            {organizations.length > 1 && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="organization">Organization</Label>
                <select
                  id="organization"
                  data-field="organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className={selectClass}
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. Standard Speed Barrier"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                data-field="description"
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                data-field="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={selectClass}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="widthMm">Width (mm)</Label>
                <Input
                  id="widthMm"
                  data-field="width-mm"
                  type="number"
                  min="1"
                  placeholder="e.g. 2000"
                  value={widthMm}
                  onChange={(e) => setWidthMm(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="heightMm">Height (mm)</Label>
                <Input
                  id="heightMm"
                  data-field="height-mm"
                  type="number"
                  min="1"
                  placeholder="e.g. 1000"
                  value={heightMm}
                  onChange={(e) => setHeightMm(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="depthMm">Depth (mm, optional)</Label>
              <Input
                id="depthMm"
                data-field="depth-mm"
                type="number"
                min="1"
                placeholder="e.g. 50"
                value={depthMm}
                onChange={(e) => setDepthMm(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting || !organizationId}>
                {submitting ? "Creating..." : "Create Asset Type"}
              </Button>
              <Link to="/asset-types" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
